import { useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { flushSync } from 'react-dom';
import type { Role } from '../../../../models/pa-egna-ben/role.enum';
import PageIntroduction from '../../Shared/PageIntroduction';
import InformationBox from '../../Shared/InformationBox';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import type IValidatable from '../../../Shared/validatable.interface';
import ValidationInfo from '../../../../validation/validation-info';
import useFetch, { Status } from '../../../../hooks/fetch.hook';
import { UserApiModel } from '../../models/user.api-model';
import ValidationEngine from '../../../../validation/validation-engine';
import { AnalyticsService } from '../../../../services/analytics-service';
import ValidationSummary from '../../../Shared/ValidationSummary';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';
import AntiforgeryService from '../../../../services/anti-forgery.service';

function DeleteAccount({ role }: { role: Role }) {
  // === STATE ===
  const [deleteStatus, setDeleteStatus] = useState<Status>(Status.notperformed);
  const [deleteErrorCode, setDeleteErrorCode] = useState<number | undefined>(undefined);
  const [getCurrentUser] = useFetch<UserApiModel>(`/api/paegnaben/${role}/current`);
  const [emailAddressState, setEmailAddressState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [validationSummaryErrors, setValidationSummaryErrors] = useState<string[]>([]);

  // === REFS ===
  const emailAddressRef = useRef<IValidatable>(null);

  // === DATA ===
  const user = getCurrentUser.data ? UserApiModel.mapToBusinessModel(getCurrentUser.data) : undefined;

  // === VALIDATION ===
  function validateEmailAddress(): ValidationInfo {
    const validationInfo = new ValidationInfo(`E-postadress - `);
    ValidationEngine.for(validationInfo)
      .required(user?.emailAddress)
      .equal(emailAddressState.value, user!.emailAddress, 'Den ifyllda e-postadressen matchar inte din e-postadress', (x) => x) // Avoid translation
      .validate();
    return validationInfo;
  }

  // === EVENT HANDLERS ===
  async function handleDeleteButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();
    let isFormValid: boolean | undefined;
    await flushSync(async () => {
      if (deleteStatus === Status.loading) return;
      setDeleteErrorCode(undefined);
      const validationInfo = validateEmailAddress();
      setValidationSummaryErrors(validationInfo.getErrorsWithPrefix());
      if (!validationInfo.isValid) {
        return;
      }
      isFormValid = true;
      setDeleteStatus(Status.loading);
      try {
        const response = await fetch(`/api/paegnaben/${role}/current`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
        });
        if (response.ok) {
          setDeleteStatus(Status.success);
          setDeleteErrorCode(undefined);
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Konto avslutat', '');

          // Create and submit a form to log out the user
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = '/konto/logga-ut';
          form.style.display = 'none';

          const tokenInput = document.createElement('input');
          tokenInput.type = 'hidden';
          tokenInput.name = '__RequestVerificationToken';
          tokenInput.value = await AntiforgeryService.getToken();
          form.appendChild(tokenInput);

          document.body.appendChild(form);
          form.submit();
        } else {
          setDeleteStatus(Status.failed);
          setDeleteErrorCode(response.status);
          AnalyticsService.addEvent(AnalyticsService.PaEgnaBenTeacher, 'Konto avslutat - misslyckat', '');
        }
      } catch {
        setDeleteStatus(Status.failed);
      }
    });
    if (!isFormValid || deleteErrorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  // === RENDER ===
  if (getCurrentUser.httpResponseStatusCode === 401 || deleteErrorCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getCurrentUser.httpResponseStatusCode === 403 || deleteErrorCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }

  return (
    <div>
      <PageIntroduction heading="Avsluta konto" />
      <InformationBox
        type={InformationBoxType.Danger}
        headingElement={<span className="fw-bold">Avsluta konto</span>}
        textElement={
          <div>
            <p>När du avslutar ditt konto förlorar du möjligheten att hantera klassuppgifter och rapportera in resultat.</p>
            <p className="mb-0">Bekräfta genom att fylla i din e-postadress nedan.</p>
          </div>
        }
        className="mb-4 mb-md-5"
      ></InformationBox>
      <form id="form">
        <fieldset>
          <legend className="visually-hidden">Formulär för att bekräfta avsluta konto</legend>
          <ValidationSummary id="form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
          <TextFieldFormGroup
            ref={emailAddressRef}
            labelText="E-postadress"
            required={true}
            hideRequiredAsterisk={true}
            inputId="email-address-input"
            type="text"
            serverValidationName="emailAddress"
            state={[emailAddressState, setEmailAddressState]}
          />
          <div className="d-grid d-md-flex gap-3">
            <Link to="/" className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1">
              Avbryt
            </Link>
            <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0 order-1 order-md-2" onClick={handleDeleteButtonClick}>
              Avsluta konto
            </button>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default DeleteAccount;
